import React, { useCallback, useMemo } from 'react';

import { difference, isNil, startsWith, toLower, xor } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { youthItemsSel } from '@modules/advancement/common';
import {
  selectedRowKeysSel,
  setSelectedRowKeys,
} from '@modules/advancement/pendingItems/duck';
import { usePendingItemsRequirementsQuery } from '@modules/advancement/pendingItems/hooks';
import type {
  GroupedPendingItem,
  GroupedPendingItemChild,
  PendingItem,
  TableClickActions,
} from '@modules/advancement/pendingItems/types';
import {
  groupPendingItems,
  isYouthInUserSubunitChecker,
} from '@modules/advancement/pendingItems/utils';
import { achieveFirstClassAndAbove } from '@modules/advancement/purchaseOrder/utils';
import { TableMobile, offlineSel } from '@modules/shared';
import { programNames, programTypeName } from '@modules/shared/constants';
import {
  selectedOrganizationSel,
  shouldFilterUsersBySubunitsSel,
} from '@modules/shared/duck/selectors';

import { userSubUnitMembersSel } from '../../../../packRoster/duck';
import { PendingRequirementsListFab } from './PendingRequirementsListFab';
import { rednerHeaderRow } from './PendingRequirementsListHeaderRow';
import PendingRequirementsListNotApprovable from './PendingRequirementsListNotApprovable';
import { renderDefaultRows } from './PendingRequirementsListRows';

export type FlatListItem =
  | GroupedPendingItemChild
  | {
      row: JSX.Element;
      key: string;
      type: string;
    };

interface PendingRequirementsListProps {
  onGoToYouthProfile: TableClickActions['onGoToYouthProfile'];
  search: string;
  inlineFab?: boolean;
}

export const PendingRequirementsListOriginal: React.FC<
  PendingRequirementsListProps
> = props => {
  const { inlineFab, onGoToYouthProfile, search } = props;
  const dispatch = useDispatch();
  const selectedRowKeys = useSelector(selectedRowKeysSel) as string[];
  const isOffline = useSelector(offlineSel);
  const currentOrg = useSelector(selectedOrganizationSel);
  const rosterItems = useSelector(youthItemsSel);
  const userSubUnitMembers = useSelector(userSubUnitMembersSel);
  const shouldFilterUsersBySubunits = useSelector(
    shouldFilterUsersBySubunitsSel,
  );
  const {
    data: pendingItemsData,
    isLoading,
    isFetching,
  } = usePendingItemsRequirementsQuery();
  const items = useMemo(() => {
    if (!pendingItemsData) return [];

    return groupPendingItems(pendingItemsData).filter(
      ({ memberId, name }) =>
        toLower(name).includes(toLower(search)) ||
        startsWith(memberId.toString(), search),
    );
  }, [pendingItemsData, search]);

  const userSubUnitUsersMIDs: number[] = useMemo(
    () =>
      userSubUnitMembers
        .map(member => member.memberId)
        .filter(memberId => !isNil(memberId)) as number[],
    [userSubUnitMembers],
  );

  const addNeededFields = items.map(youth => ({
    ...youth,
    firstClassAndAbove: achieveFirstClassAndAbove(youth.memberId, rosterItems),
    children: youth.children.map(item => ({
      ...item,
      isUserInSubUnit: isYouthInUserSubunitChecker(
        shouldFilterUsersBySubunits,
        userSubUnitUsersMIDs,
        youth.memberId,
      ),
    })),
  }));

  const notApprovableItemsWithChildren = addNeededFields
    .map(youth => {
      if (youth.firstClassAndAbove) {
        return {
          ...youth,
          children: youth.children.filter(
            adv =>
              adv.program !== currentOrg.programType &&
              adv.program !== programNames.BOY_SCOUT,
          ),
        };
      } else {
        return {
          ...youth,
          children: youth.children.filter(
            adv => adv.program !== currentOrg.programType,
          ),
        };
      }
    })
    .filter(youth => youth.children.length > 0);

  const showNotApprovable =
    currentOrg.programType !== programTypeName.CUB_SCOUT &&
    notApprovableItemsWithChildren.length > 0;

  const onSelectItem = useCallback(
    (record: GroupedPendingItem | PendingItem) => {
      const selectedItems = [record.key];

      dispatch(setSelectedRowKeys(xor(selectedRowKeys, selectedItems)));
    },
    [dispatch, selectedRowKeys],
  );

  const onSelectGroup = useCallback(
    (groupKey: string) => {
      const group = items.find(({ key }) => key === groupKey);
      const groupItems = group?.children.map(({ key }) => key);
      const selectedInGroup = selectedRowKeys.filter(item =>
        groupItems?.includes(item),
      );
      const diff = difference(groupItems, selectedRowKeys);

      if (groupItems?.length === selectedInGroup.length) {
        return dispatch(setSelectedRowKeys(xor(selectedRowKeys, groupItems)));
      }

      return dispatch(setSelectedRowKeys(xor(selectedRowKeys, diff)));
    },
    [dispatch, items, selectedRowKeys],
  );

  const flatItems = useMemo(
    () =>
      items.reduce<FlatListItem[]>((result, item) => {
        result.push({
          row: rednerHeaderRow({
            isOffline,
            item,
            onGoToYouthProfile,
            onSelectGroup,
            selectedRowKeys,
          }),
          key: item.key,
          type: 'custom',
        });

        item.children.forEach(row => {
          result.push(row);
        });

        return result;
      }, []),
    [isOffline, items, onGoToYouthProfile, onSelectGroup, selectedRowKeys],
  );

  return (
    <>
      <TableMobile
        items={flatItems}
        loading={isLoading || isFetching}
        onItemClick={onSelectItem}
        rowId="key"
        rows={renderDefaultRows()}
        selectedKeys={selectedRowKeys}
      />
      <PendingRequirementsListFab
        isOffline={isOffline}
        items={flatItems}
        inline={inlineFab}
      />
      {showNotApprovable && (
        <PendingRequirementsListNotApprovable
          isLoading={isLoading}
          isOffline={isOffline}
          notApprovableItemsWithChildren={notApprovableItemsWithChildren}
          onGoToYouthProfile={onGoToYouthProfile}
          searchQuery={search}
        />
      )}
    </>
  );
};

export const PendingRequirementsList = React.memo(
  PendingRequirementsListOriginal,
);
